// The group's activation link ("magic link", DR-405) as the guide and the signatory share it. Non-visual and framework-free,
// so both apps behave the same and the logic is testable without a DOM.

/** Why a link is closed; mirrors the API. */
export type InvitationClosure = 'GROUP_CLOSED' | 'NO_TRIP_DATE' | 'EXPIRED' | 'EVERYONE_ACTIVE';

/** `GET …/activation-invitation`. */
export interface Invitation {
  groupName: string;
  /** The shareable token; null while the link is closed. */
  token: string | null;
  /** The passengers' site of the group's country; null when the server does not know it. */
  appUrl: string | null;
  open: boolean;
  closedReason: InvitationClosure | null;
  closesAt: string | null;
  total: number;
  pending: number;
}

/** Route of the passenger app that receives the link. The token travels in the URL fragment: no server log or Referer sees it. */
export const ACTIVATION_ROUTE = '/activar';

export function activationLinkUrl(appUrl: string, token: string): string {
  return `${appUrl.replace(/\/+$/, '')}${ACTIVATION_ROUTE}#${token}`;
}

/** The link to share, or null when there is nothing to share (closed). `fallbackAppUrl` is used when the server has no site address configured. */
export function invitationUrl(invitation: Invitation, fallbackAppUrl: string): string | null {
  if (!invitation.open || invitation.token === null) return null;
  return activationLinkUrl(invitation.appUrl ?? fallbackAppUrl, invitation.token);
}

/** The message that goes with the link in WhatsApp and the like. */
export function invitationMessage(groupName: string): string {
  return `¡Hola! Activá tu cuenta de Funtour para elegir tu habitación en el grupo ${groupName}. Entrá con este enlace y tené a mano tu documento:`;
}

export type ShareOutcome = 'shared' | 'opened-whatsapp' | 'cancelled';

/**
 * Shares through the device's share sheet when there is one (phones: WhatsApp is one tap away); on a computer it opens
 * WhatsApp Web / the app with the message already written. A person who closes the sheet is not an error.
 */
export async function shareInvitation(url: string, message: string): Promise<ShareOutcome> {
  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    try {
      await navigator.share({ text: message, url });
      return 'shared';
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return 'cancelled';
      // Any other failure (blocked, unsupported data): fall through to the WhatsApp address.
    }
  }
  window.open(`https://wa.me/?text=${encodeURIComponent(`${message} ${url}`)}`, '_blank', 'noopener,noreferrer');
  return 'opened-whatsapp';
}

/** Copies text; true when it was copied (the Clipboard API needs HTTPS and the person's permission). */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
